// src/hooks/useWorkQueries.js
// React Query hooks for the unified Work API (Tasks • Jobs • Appointments)
// -----------------------------------------------------------------------------
// Depends on: @tanstack/react-query (v5) + src/services/workService.js
//
// Exposes:
//   - WORK_KEYS
//   - WORK_SORT_MAP
//   - useWorkList(params, options)
//   - useWork(id, options)
//   - useCreateWork(options)
//   - useUpdateWork(options)
//   - useTransitionWork(options)
//   - useDeleteWork(options)
//   - useAttachQuote(options)
//   - useAttachInvoice(options)
//   - useChildWorks(parentWorkId, { type, ...options })
//   - useUnscheduledWork(params, options)
//   - useScheduleWork(options)
//   - useUnscheduleWork(options)
//   - usePrepareWorkSend({ id, type, ... })
//   - useSendWorkNotice({ id, type, ... })
//   - usePreviewWorkNotice({ id, type, ... })
// -----------------------------------------------------------------------------

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';

import {
  listWork,
  getWork,
  createWork,
  updateWork,
  transitionWork,
  deleteWork,
  attachQuote,
  attachInvoice,
  scheduleWork,     // NEW
  unscheduleWork,   // NEW
  listUnscheduled,  // NEW
  // ✉️ Email actions
  prepareWorkSend,
  sendWorkNotice,
  previewWorkNotice,
} from '../services/workService';

/* --------------------------------- keys ---------------------------------- */

function stableKey(obj = {}) {
  const entries = Object.entries(obj).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  );
  entries.sort(([a], [b]) => (a > b ? 1 : a < b ? -1 : 0));
  const normalized = entries.map(([k, v]) => [
    k,
    Array.isArray(v) ? v.join('|') : String(v),
  ]);
  return JSON.stringify(normalized);
}

export const WORK_KEYS = {
  root: ['work'],
  lists: (params = {}) => ['work', 'list', stableKey(params)],
  detail: (id) => ['work', 'detail', String(id || '')],
  unscheduled: (params = {}) => ['work', 'unscheduled', stableKey(params)],
  calendarRoot: ['calendar'],
  // ✉️ Email composer/prepare cache (keyed by work + type)
  emailPrepare: (id, type = 'confirm') => [
    'work',
    'email',
    'prepare',
    String(id || ''),
    String(type || 'confirm'),
  ],
};

/* ------------------------------ local utils ------------------------------- */

const getId = (obj) => obj?.id || obj?._id;
const toId = (v) =>
  v == null
    ? ''
    : typeof v === 'string' || typeof v === 'number'
    ? String(v)
    : String(v.id || v._id || v.userId || '');

function callIf(fn, ...args) {
  if (typeof fn === 'function') fn(...args);
}

function prettyStatus(s) {
  if (!s) return '';
  return String(s)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

/** Fallback FSM (keep in sync with server) */
const TRANSITIONS = Object.freeze({
  task: {
    todo: ['in_progress', 'blocked', 'cancelled'],
    in_progress: ['done', 'blocked', 'cancelled'],
    blocked: ['in_progress', 'cancelled'],
    done: [],
    cancelled: [],
  },
  job: {
    new: ['scheduled', 'cancelled'],
    scheduled: ['en_route', 'in_progress', 'cancelled', 'no_show'],
    en_route: ['in_progress', 'cancelled', 'no_show'],
    in_progress: ['completed', 'cancelled'],
    completed: ['invoiced'],
    invoiced: [],
    cancelled: [],
    no_show: ['scheduled', 'cancelled'],
  },
  appointment: {
    planned: ['confirmed', 'cancelled'],
    confirmed: ['in_progress', 'cancelled', 'no_show'],
    in_progress: ['done', 'cancelled'],
    done: [],
    cancelled: [],
    no_show: ['planned', 'cancelled'],
  },
});

function fsmAllowed(type, status) {
  const m = TRANSITIONS[String(type) || ''];
  return m ? m[String(status) || ''] || [] : [];
}

/* ------------------------------ sort mapping ------------------------------ */
export const WORK_SORT_MAP = Object.freeze({
  workNumber: 'workNumber',
  title: 'title',
  client: 'sortKeys.clientName',
  assignee: 'sortKeys.primaryAssigneeName',
  'schedule.start': 'sortKeys.start',
  'schedule.end': 'sortKeys.end',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
});

function translateSort(sort) {
  if (!sort) return undefined;
  const [fieldRaw, dirRaw] = String(sort).split(':');
  const field = WORK_SORT_MAP[fieldRaw] || fieldRaw || 'createdAt';
  const dir = dirRaw === 'asc' ? 'asc' : 'desc';
  return `${field}:${dir}`;
}

/* ------------------------------- param shape ------------------------------ */

function toCsv(val) {
  if (!val) return undefined;
  if (Array.isArray(val)) {
    const parts = val.map((s) => String(s).trim()).filter(Boolean);
    return parts.length ? parts.join(',') : undefined;
  }
  return String(val);
}

/**
 * Normalize list params for API + query key.
 */
function normalizeListParams(p = {}) {
  return {
    // filters
    q: p.q || undefined,
    type: p.type || undefined,
    status: toCsv(p.status),
    priority: toCsv(p.priority),
    assigneeId: p.assigneeId || undefined,
    clientId: p.clientId || undefined,
    assigneeName: p.assigneeName || undefined,
    clientName: p.clientName || undefined,
    from: p.from || undefined,
    to: p.to || undefined,
    includeCancelled: p.includeCancelled ? '1' : undefined,

    // paging/sort
    page: p.page || 1,
    limit: p.limit || 25,
    sort: translateSort(p.sort),

    // extra passthroughs (e.g., parentWorkId for children)
    parentWorkId: p.parentWorkId || undefined,
  };
}

/* ---------------------------- DTO transformation --------------------------- */

function toWorkDTO(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  const scheduleRaw = raw.schedule && typeof raw.schedule === 'object' ? raw.schedule : {};
  const schedule = {
    start: scheduleRaw.start || null,
    end: scheduleRaw.end || null,
    allDay: !!scheduleRaw.allDay,
    // 🔧 do NOT force a default timezone here; keep what BE sends
    timezone: scheduleRaw.timezone ?? null,
    rrule: scheduleRaw.rrule || undefined,
  };
  const start = schedule.start ? new Date(schedule.start) : undefined;
  const end = schedule.end ? new Date(schedule.end) : undefined;
  const allDay = schedule.allDay;
  const timezone = schedule.timezone;

  const assigneesArr = Array.isArray(raw.assignees)
    ? raw.assignees
    : raw.assignees
    ? [raw.assignees]
    : [];
  const assignees = assigneesArr.map((a) => toId(a)).filter(Boolean);
  const assigneeSnapshots = assigneesArr
    .filter((a) => a && typeof a === 'object')
    .map((a) => ([
      toId(a),
      a.name || a.fullName || a.displayName || (a.email ? String(a.email).split('@')[0] : undefined),
      a.avatarUrl || undefined
    ]))
    .map(([id, name, avatarUrl]) => ({ id, name, avatarUrl }));

  const links = {
    quoteId: raw.quoteId || undefined,
    invoiceId: raw.invoiceId || undefined,
    quoteSnapshot: raw.quote
      ? { id: toId(raw.quote), number: raw.quote.number || raw.quote.code || undefined }
      : undefined,
    invoiceSnapshot: raw.invoice
      ? { id: toId(raw.invoice), number: raw.invoice.number || raw.invoice.code || undefined }
      : undefined,
  };

  const statusTimeline = (Array.isArray(raw.statusTimeline) ? raw.statusTimeline : [])
    .map((sp) => ({
      status: sp?.status,
      startedAt: sp?.startedAt || null,
      endedAt: sp?.endedAt || null,
      reason: sp?.reason || undefined,
      actor: sp?.actor
        ? { id: toId(sp.actor.id || sp.actor), name: sp.actor.name || undefined }
        : sp?.actorUserId
        ? { id: toId(sp.actorUserId) }
        : undefined,
    }))
    .sort((a, b) => {
      const ta = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const tb = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return ta - tb;
    });

  const currentSince = raw.currentStatusSince ? new Date(raw.currentStatusSince) : undefined;

  // Client snapshot (accepts raw.clientSnapshot too)
  const srcClient = raw.client || raw.clientSnapshot || null;
  const clientSnapshot = srcClient
    ? {
        id: toId(srcClient),
        name: srcClient.name || srcClient.displayName || undefined,
        email: srcClient.email || undefined,
        phone: srcClient.phone || undefined,
        address:
          srcClient.address ||
          srcClient.primaryAddress ||
          srcClient.billingAddress ||
          undefined,
        primaryAddress: srcClient.primaryAddress || undefined,
        billingAddress: srcClient.billingAddress || undefined,
        addresses: Array.isArray(srcClient.addresses) ? srcClient.addresses : undefined,
      }
    : raw.clientId
    ? { id: toId(raw.clientId) }
    : undefined;

  const sortKeys = {
    clientName: (raw.client?.name || raw.clientSnapshot?.name || ''),
    start: schedule.start || null,
    end: schedule.end || null,
  };

  const allowedFromServer = Array.isArray(raw.allowedNextStatuses) ? raw.allowedNextStatuses : [];
  const allowedFromFSM = fsmAllowed(raw.type, raw.status);
  let allowedNextStatuses = Array.from(new Set([...allowedFromServer, ...allowedFromFSM]));
  if (raw.type === 'job' && !raw.invoiceId) {
    allowedNextStatuses = allowedNextStatuses.filter((s) => s !== 'invoiced');
  }

  // Prefer site address on work
  const workAddress = raw.workAddress || raw.address || undefined;

  return {
    id: raw.id || raw._id,
    workNumber: raw.workNumber,
    type: raw.type,
    title: raw.title,
    description: raw.description,
    priority: raw.priority,
    status: raw.status,
    statusLabel: raw.statusLabel || prettyStatus(raw.status),
    allowedNextStatuses,

    parentWorkId: raw.parentWorkId ? toId(raw.parentWorkId) : undefined,

    schedule,
    start,
    end,
    allDay,
    timezone,

    clientSnapshot,
    assignees,
    assigneeSnapshots,

    links,
    emailStats: raw.emailStats ? { ...raw.emailStats } : undefined,
    hasQuote: !!raw.quoteId,
    hasInvoice: !!raw.invoiceId,

    jobProfile: raw.jobProfile ? { ...raw.jobProfile } : undefined,
    taskProfile: raw.taskProfile ? { ...raw.taskProfile } : undefined,

    tags: Array.isArray(raw.tags) ? raw.tags : [],

    // expose both for compatibility; UI reads work.workAddress
    workAddress,
    address: workAddress,

    statusTimeline,
    currentStatusSince: raw.currentStatusSince || null,
    computedTimeInStatusMs: currentSince ? Date.now() - currentSince.getTime() : undefined,
    meta: {
      createdAt: raw.createdAt || null,
      updatedAt: raw.updatedAt || null,
      softDeleted: !!raw.softDeleted,
    },

    sortKeys,
  };
}

/** Optimistic status helper */
function withOptimisticStatus(dto, nextStatus) {
  const nowIso = new Date().toISOString();
  const timeline = Array.isArray(dto?.statusTimeline) ? dto.statusTimeline.map((x) => ({ ...x })) : [];

  for (let i = timeline.length - 1; i >= 0; i--) {
    if (!timeline[i].endedAt) {
      timeline[i] = { ...timeline[i], endedAt: nowIso };
      break;
    }
  }
  timeline.push({ status: nextStatus, startedAt: nowIso, endedAt: null, actor: undefined });

  return {
    ...dto,
    status: nextStatus,
    statusLabel: prettyStatus(nextStatus),
    allowedNextStatuses: fsmAllowed(dto?.type, nextStatus),
    statusTimeline: timeline,
    currentStatusSince: nowIso,
  };
}

/* ---------------------------- optimistic guards --------------------------- */

// Expanded whitelist for inline edits
const UPDATE_WHITELIST = new Set([
  'title',
  'priority',
  'schedule',
  'assignees',
  'tags',
  'customFields',
  'jobProfile',
  'parentWorkId',
  'address',       // legacy
  'workAddress',   // NEW: allow inline site address edits
]);

function buildSafePatch(patch) {
  if (!patch || typeof patch !== 'object') return {};
  const out = {};
  for (const k of Object.keys(patch)) {
    if (UPDATE_WHITELIST.has(k)) out[k] = patch[k];
  }
  delete out.status; // status must go via transition endpoint
  return out;
}

function toIsoMaybe(v) {
  if (!v) return v;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isFinite(d.getTime()) ? d.toISOString() : v;
}

function normalizeScheduleOptimistic(s = {}) {
  if (!s || typeof s !== 'object') return {};
  const next = { ...s };
  if ('start' in next) next.start = toIsoMaybe(next.start);
  if ('end' in next) next.end = toIsoMaybe(next.end);
  // 🔧 don't inject timezone here; caller/BE controls it
  if (typeof next.allDay !== 'boolean') next.allDay = false;
  return next;
}

/* -------------------------------- queries -------------------------------- */

export function useWorkList(params = {}, options = {}) {
  const srvParams = normalizeListParams(params);

  const query = useQuery({
    queryKey: WORK_KEYS.lists(srvParams),
    queryFn: ({ signal }) => listWork(srvParams, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    ...options,
  });

  const itemsRaw = query.data?.items || [];
  const items = itemsRaw.map(toWorkDTO);

  const meta = query.data?.meta ?? {
    page: query.data?.page ?? 1,
    limit: query.data?.limit ?? items.length,
    total: query.data?.total ?? items.length,
    totalPages: query.data?.totalPages ?? 1,
  };

  return { ...query, items, meta };
}

export function useWork(id, options = {}) {
  const { select: userSelect, ...rest } = options || {};
  const baseSelect = (raw) => toWorkDTO(raw);
  const finalSelect = userSelect ? (raw) => userSelect(baseSelect(raw)) : baseSelect;

  return useQuery({
    queryKey: WORK_KEYS.detail(id),
    queryFn: ({ signal }) => getWork(id, { signal }),
    enabled: Boolean(id) && (rest.enabled ?? true),
    staleTime: 15_000,
    select: finalSelect,
    ...rest,
  });
}

/**
 * Child works under a parent (e.g., tasks for a job)
 */
export function useChildWorks(
  parentWorkId,
  { type, enabled = true, limit = 100, ...options } = {}
) {
  const params = normalizeListParams({
    parentWorkId: parentWorkId ? String(parentWorkId) : undefined,
    type,
    limit,
    sort: 'schedule.start:asc,createdAt:desc',
  });
  return useWorkList(params, { enabled: enabled && !!parentWorkId, ...options });
}

/**
 * Unscheduled tray (tasks + jobs)
 */
export function useUnscheduledWork(params = {}, options = {}) {
  const srvParams = {
    q: params.q || undefined,
    type: params.type || undefined,
    status: toCsv(params.status),
    priority: toCsv(params.priority),
    assigneeId: params.assigneeId || undefined,
    clientId: params.clientId || undefined,
    includeCancelled: params.includeCancelled ? '1' : undefined,
    page: params.page || 1,
    limit: params.limit || 100,
    sort: translateSort(params.sort || 'createdAt:desc'),
  };

  const query = useQuery({
    queryKey: WORK_KEYS.unscheduled(srvParams),
    queryFn: ({ signal }) => listUnscheduled(srvParams, { signal }),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    ...options,
  });

  const items = (query.data?.items || []).map(toWorkDTO);
  const meta = query.data?.meta ?? {
    page: query.data?.page ?? 1,
    limit: query.data?.limit ?? items.length,
    total: query.data?.total ?? items.length,
    totalPages: query.data?.totalPages ?? 1,
  };

  return { ...query, items, meta };
}

/* ------------------------------- mutations ------------------------------- */

export function useCreateWork(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload) => createWork(payload),
    onSuccess: (created, vars, ctx) => {
      const id = getId(created);
      if (id) qc.setQueryData(WORK_KEYS.detail(id), toWorkDTO(created));
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, toWorkDTO(created), vars, ctx);
    },
    onError: (err, vars, ctx) => {
      callIf(options.onError, err, vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

export function useUpdateWork(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }) => updateWork(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: WORK_KEYS.detail(id) });
      const prev = qc.getQueryData(WORK_KEYS.detail(id));
      const safe = buildSafePatch(patch);

      if (prev) {
        const nextRaw = {
          ...prev,
          ...safe,
          ...(safe.schedule ? { schedule: { ...safe.schedule } } : {}),
        };
        const nextDTO = toWorkDTO(nextRaw);
        qc.setQueryData(WORK_KEYS.detail(id), nextDTO);
      }
      return { prev, id };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(WORK_KEYS.detail(ctx.id), ctx.prev);
      callIf(options.onError, err, vars, ctx);
    },
    onSuccess: (updated, vars, ctx) => {
      const id = getId(updated) || vars?.id;
      if (id) qc.setQueryData(WORK_KEYS.detail(id), toWorkDTO(updated));
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, toWorkDTO(updated), vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

export function useTransitionWork(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }) => transitionWork(id, status),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: WORK_KEYS.detail(id) });
      const prev = qc.getQueryData(WORK_KEYS.detail(id));
      if (prev) {
        const optimistic = withOptimisticStatus(prev, status);
        qc.setQueryData(WORK_KEYS.detail(id), optimistic);
      }
      return { prev, id };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(WORK_KEYS.detail(ctx.id), ctx.prev);
      callIf(options.onError, err, vars, ctx);
    },
    onSuccess: (saved, vars, ctx) => {
      const id = getId(saved) || vars?.id;
      if (id) qc.setQueryData(WORK_KEYS.detail(id), toWorkDTO(saved));
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, toWorkDTO(saved), vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

export function useDeleteWork(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteWork(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: WORK_KEYS.detail(id) });
      const prev = qc.getQueryData(WORK_KEYS.detail(id));
      qc.removeQueries({ queryKey: WORK_KEYS.detail(id) });
      return { prev, id };
    },
    onError: (err, id, ctx) => {
      if (ctx?.prev) qc.setQueryData(WORK_KEYS.detail(ctx.id), ctx.prev);
      callIf(options.onError, err, id, ctx);
    },
    onSuccess: (_deleted, id, ctx) => {
      qc.removeQueries({ queryKey: WORK_KEYS.detail(id) });
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, _deleted, id, ctx);
    },
    onSettled: (data, error, id, ctx) => {
      callIf(options.onSettled, data, error, id, ctx);
    },
  });
}

/* ------------------------------ linkage hooks ----------------------------- */

export function useAttachQuote(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, quoteId }) => attachQuote(id, quoteId),
    onSuccess: (saved, vars, ctx) => {
      const id = getId(saved) || vars?.id;
      if (id) qc.setQueryData(WORK_KEYS.detail(id), toWorkDTO(saved));
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, toWorkDTO(saved), vars, ctx);
    },
    onError: (err, vars, ctx) => {
      callIf(options.onError, err, vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

export function useAttachInvoice(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, invoiceId }) => attachInvoice(id, invoiceId),
    onSuccess: (saved, vars, ctx) => {
      const id = getId(saved) || vars?.id;
      if (id) qc.setQueryData(WORK_KEYS.detail(id), toWorkDTO(saved));
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, toWorkDTO(saved), vars, ctx);
    },
    onError: (err, vars, ctx) => {
      callIf(options.onError, err, vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

/* --------------------------- scheduling mutations -------------------------- */

export function useScheduleWork(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (vars) => scheduleWork(vars.id, vars),
    onMutate: async (vars) => {
      const { id, start, end, timezone, allDay, assigneeIds } = vars || {};
      await qc.cancelQueries({ queryKey: WORK_KEYS.detail(id) });
      const prev = qc.getQueryData(WORK_KEYS.detail(id));

      if (prev) {
        const nextSchedule = normalizeScheduleOptimistic({ start, end, timezone, allDay });
        const nextRaw = {
          ...prev,
          schedule: nextSchedule,
          assignees: Array.isArray(assigneeIds) && assigneeIds.length ? assigneeIds : prev.assignees,
        };
        const nextDTO = toWorkDTO(nextRaw);
        qc.setQueryData(WORK_KEYS.detail(id), nextDTO);
      }

      return { prev, id };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(WORK_KEYS.detail(ctx.id), ctx.prev);
      if (err && err.status === 409) {
        const conflicts = err.conflicts || err.data?.conflicts || {};
        callIf(options.onConflict, {
          error: err,
          suggestions: conflicts.suggestions || [],
          reasons: conflicts.reasons || [],
        });
        return;
      }
      callIf(options.onError, err, vars, ctx);
    },
    onSuccess: (_res, vars, ctx) => {
      qc.invalidateQueries({ queryKey: WORK_KEYS.detail(vars.id) });
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, _res, vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

export function useUnscheduleWork(options = {}) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id) => unscheduleWork(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: WORK_KEYS.detail(id) });
      const prev = qc.getQueryData(WORK_KEYS.detail(id));

      if (prev) {
        const nextRaw = {
          ...prev,
          schedule: {
            ...(prev.schedule || {}),
            start: null,
            end: null,
          },
        };
        const nextDTO = toWorkDTO(nextRaw);
        qc.setQueryData(WORK_KEYS.detail(id), nextDTO);
      }
      return { prev, id };
    },
    onError: (err, id, ctx) => {
      if (ctx?.prev) qc.setQueryData(WORK_KEYS.detail(ctx.id), ctx.prev);
      callIf(options.onError, err, id, ctx);
    },
    onSuccess: (saved, id, ctx) => {
      const savedId = getId(saved) || id;
      if (savedId) qc.setQueryData(WORK_KEYS.detail(savedId), toWorkDTO(saved));
      qc.invalidateQueries({ queryKey: WORK_KEYS.root });
      qc.invalidateQueries({ queryKey: WORK_KEYS.unscheduled({}) });
      qc.invalidateQueries({ queryKey: WORK_KEYS.calendarRoot });
      callIf(options.onSuccess, toWorkDTO(saved), id, ctx);
    },
    onSettled: (data, error, id, ctx) => {
      callIf(options.onSettled, data, error, id, ctx);
    },
  });
}

/* ------------------------------ ✉️ email hooks ----------------------------- */

/** Prefill recipients/subject/message + lastSentAt for chips */
export function usePrepareWorkSend(
  { id, type = 'confirm' },
  options = {}
) {
  return useQuery({
    queryKey: WORK_KEYS.emailPrepare(id, type),
    queryFn: () => prepareWorkSend(id, { type }),
    enabled: Boolean(id) && (options.enabled ?? true),
    staleTime: options.staleTime ?? 10_000,
    ...options,
  });
}

/** Send Confirmation/Reminder/Completion and refresh the prepare cache */
export function useSendWorkNotice(options = {}) {
  const qc = useQueryClient();
  const ALL_TYPES = ['confirm', 'reminder', 'complete'];

  return useMutation({
    mutationFn: (vars) => {
      // Accept shapes: { id, type, ...payload }
      const { id, type, ...payload } = vars || {};
      return sendWorkNotice(id, { type, ...payload });
    },
    onSuccess: (result, vars, ctx) => {
      const id = vars?.id || result?.work?.id;
      if (id) {
        // Refresh all prepare caches so "Last sent • x days ago" updates immediately
        ALL_TYPES.forEach((t) =>
          qc.invalidateQueries({ queryKey: WORK_KEYS.emailPrepare(id, t) })
        );
        // Also refresh the work detail in case any server-side fields changed
        qc.invalidateQueries({ queryKey: WORK_KEYS.detail(id) });
      }
      callIf(options.onSuccess, result, vars, ctx);
    },
    onError: (err, vars, ctx) => {
      callIf(options.onError, err, vars, ctx);
    },
    onSettled: (data, error, vars, ctx) => {
      callIf(options.onSettled, data, error, vars, ctx);
    },
  });
}

/** Lightweight preview (mostly for text fallback) */
export function usePreviewWorkNotice(
  { id, type = 'confirm', ...payload },
  options = {}
) {
  return useQuery({
    queryKey: ['work', 'email', 'preview', String(id || ''), String(type || 'confirm'), stableKey(payload)],
    queryFn: () => previewWorkNotice(id, { type, ...payload }),
    enabled: Boolean(id) && (options.enabled ?? false),
    staleTime: options.staleTime ?? 5_000,
    ...options,
  });
}

export { fsmAllowed };
